import { type ChangeEventHandler, memo } from "react";

import { Icon } from "@react/components/Icon";
import { Text } from "@react/components/Text";
import { tw } from "classnames";

const styles = {
  form: tw("flex", "flex-row", "rounded", "h-12"),
  textInput: tw(
    "flex-1",
    "px-3.5",
    "py-0",
    "mr-3",
    "h-full",
    "font-calibre",
    "text-base",
    "font-normal",
    "leading-snug",
    "tracking-normal",
    "antialiased",
    "border-0",
    "rounded",
    "bg-transparent",
    "focus:outline-none"
  ),
  button: tw("py-3.5", "px-5", "rounded", "border", "border-solid"),
  arrowIcon: tw("w-2.5", "h-2.5", "mt-1"),
  checkmarkIcon: tw("w-4.5", "h-4.5", "text-char-50"),
  helperIcon: tw("w-3", "h-3", "mr-1.5"),
  helperTextBox: tw(
    "flex",
    "flex-row",
    "items-center",
    "mt-1.5",
    "text-char-50"
  ),
};

type Props = {
  inputText: string;
  invalidHelperText: string;
  isFormValid: boolean;
  onChange: ChangeEventHandler;
  onSubmit: (e: React.SyntheticEvent) => void;
  placeholder: string;
  submitted: boolean;
};

const FooterEmailFormViewBase = ({
  inputText,
  invalidHelperText,
  isFormValid,
  onChange,
  onSubmit,
  placeholder,
  submitted,
}: Props) => {
  const formStyles = tw(
    isFormValid
      ? tw("bg-background-primary")
      : tw(
          "bg-interactive-error-textarea",
          "border",
          "border-solid",
          "border-interactive-error"
        ),
    styles.form
  );

  const textInputStyles = tw(
    submitted
      ? tw("text-sage-500", "placeholder:text-sage-500")
      : tw("text-primary", "placeholder:text-tertiary"),
    styles.textInput
  );

  const buttonStyles = tw(
    submitted
      ? tw("border-sage-500", "bg-sage-500")
      : isFormValid
      ? tw("border-bone-50", "bg-transparent")
      : tw("border-interactive-error-textarea", "bg-transparent"),
    styles.button
  );

  return (
    <>
      <form className={formStyles} onSubmit={onSubmit}>
        <input
          className={textInputStyles}
          onChange={onChange}
          placeholder={placeholder}
          readOnly={submitted}
          type="text"
          value={inputText}
        />
        <button className={buttonStyles}>
          {submitted ? (
            <Icon className={styles.checkmarkIcon} name="checkmark" />
          ) : (
            <Icon className={styles.arrowIcon} name="arrow" />
          )}
        </button>
      </form>
      {!isFormValid && (
        <div className={styles.helperTextBox}>
          <Icon className={styles.helperIcon} name="exclamation-circle" />
          <Text size="sm">{invalidHelperText}</Text>
        </div>
      )}
    </>
  );
};

export const FooterEmailFormView = memo(FooterEmailFormViewBase);
